import { useCallback, useEffect, useState } from "react"
import { api } from "@/shared/api-client"
import { setApprovalContext } from "@/shared/approval"

interface Pending {
  id: string
  display_name: string
  device_type: string
  browser: string
  fingerprint: string
  created: number
}
interface Reg {
  id: string
  name: string
  created: number
  expired: boolean
  blocked?: boolean
  reissue_requested?: number | null
}
interface Trusted {
  id: string
  username: string
  name: string
  device_type?: string
  fingerprint?: string
  created: number
}

const fmt = (t: number) => new Date(t * 1000).toLocaleString("ru-RU")

/**
 * Владелец: «Ожидают подтверждения» и список доверенных устройств.
 * «Разрешить» и «Отозвать» открывают окно с кодом Google Authenticator (общий механизм api-client).
 * Для остальных пользователей карточка не показывается (сервер отвечает 403).
 */
export function DeviceRequestsCard() {
  const [data, setData] = useState<{ pending: Pending[]; trusted: Trusted[] } | null>(null)
  const [regs, setRegs] = useState<Reg[]>([])
  const [error, setError] = useState("")
  const [busy, setBusy] = useState("")

  const load = useCallback(async () => {
    try {
      setData(await api.get<{ pending: Pending[]; trusted: Trusted[] }>("/devices/pending"))
      setRegs((await api.get<{ pending: Reg[] }>("/register/pending")).pending)
    } catch {
      setData(null)
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  async function act(id: string, path: string, context = "") {
    setError("")
    setBusy(id)
    setApprovalContext(context)
    try {
      await api.post(path, {})
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось")
    } finally {
      setBusy("")
    }
  }

  if (!data) return null
  const btn = "text-sm px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 disabled:opacity-50"
  return (
    <div className="box overflow-hidden p-5 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-zinc-200">Ожидают подтверждения</h2>
        <p className="text-xs text-zinc-500 mt-0.5">
          Устройства новых пользователей. Пока вы не разрешили, пользователь работает из дома без ограничений; доверенное
          устройство даёт только возможность опознать это устройство, удалённый доступ не открывается.
        </p>
      </div>
      {error && <p className="text-sm text-rose-400">{error}</p>}
      {data.pending.length === 0 && <p className="text-sm text-zinc-500">Новых запросов нет.</p>}
      {data.pending.map((p) => (
        <div key={p.id} className="rounded-lg border border-white/10 p-3 space-y-2">
          <p className="text-sm text-zinc-200">
            Зарегистрирован пользователь <b>{p.display_name}</b>. Добавить его устройство в список доверенных?
          </p>
          <p className="text-xs text-zinc-400">
            {p.device_type} · {p.browser} · {fmt(p.created)} · код устройства <code>{p.fingerprint}</code>
          </p>
          <div className="flex gap-2">
            <button className={btn} disabled={busy === p.id} onClick={() =>
                act(
                  p.id,
                  `/devices/pending/${p.id}/approve`,
                  `Добавить в доверенные устройство пользователя ${p.display_name}: ${p.device_type}, ${p.browser} (код устройства ${p.fingerprint}).`,
                )
              }>
              Разрешить
            </button>
            <button className={btn} disabled={busy === p.id} onClick={() => act(p.id, `/devices/pending/${p.id}/reject`)}>
              Отклонить
            </button>
          </div>
        </div>
      ))}
      {regs.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-zinc-200">Регистрации без входа</h3>
          <p className="text-xs text-zinc-500">Данные выданы, но человек ещё не вошёл. Новые данные придут вам в Telegram, старые перестанут работать.</p>
          {regs.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/5">
              <span className="text-sm text-zinc-300">
                {r.name}
                <span className="block text-xs text-zinc-500">{fmt(r.created)}{r.expired ? " · срок истёк" : ""}{r.blocked ? " · заблокировано" : ""}{r.reissue_requested ? " · ПРОСИТ НОВЫЕ ДАННЫЕ" : ""}</span>
              </span>
              <span className="flex gap-2">
                <button className={btn} disabled={busy === r.id} onClick={() => act(r.id, `/register/pending/${r.id}/reissue`)}>Выдать заново</button>
                <button className={btn} disabled={busy === r.id} onClick={async () => {
                  setBusy(r.id)
                  try { await api.delete(`/register/pending/${r.id}`); await load() } catch { /* ignore */ } finally { setBusy("") }
                }}>Удалить</button>
              </span>
            </div>
          ))}
        </div>
      )}
      <div>
        <h3 className="text-sm font-semibold text-zinc-200">Доверенные устройства</h3>
        {data.trusted.length === 0 && <p className="text-sm text-zinc-500 mt-1">Пока нет.</p>}
        {data.trusted.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0">
            <div className="text-sm text-zinc-300">
              {t.name}
              <span className="block text-xs text-zinc-500">
                {t.fingerprint ? `код ${t.fingerprint} · ` : ""}добавлено {fmt(t.created)}
              </span>
            </div>
            <button className={btn} disabled={busy === t.id} onClick={() => act(t.id, `/devices/trusted/${t.id}/revoke`, `ОТОЗВАТЬ доверенное устройство: ${t.name}.`)}>
              Отозвать
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
